// Settings.
//
// Section order follows iOS deliberately — profile, premium, account, privacy, Higher Self,
// notifications, appearance, data, the unlabelled destructive pair, about — so someone who set
// something up on their phone can find it in the same place here. Two things are not copied:
//
//   · iOS's Appearance "Text size" S/M/L is local @State with no persistence and no effect on
//     anything. Porting a control that does nothing would be porting a bug. The real typography
//     controls live in the reader sheet, which this screen now links to instead.
//   · iOS's "Change passphrase" calls /api/vault/keys/rewrap alone, which re-wraps the key without
//     changing the account's password hash — after which the password you sign in with and the
//     passphrase that opens the vault are two different strings. The web sends people through the
//     emailed reset instead, which the Worker handles as one transaction: new hash AND re-wrapped
//     key. Same intent, no chance of a split.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { useEntries } from '../entries/EntriesProvider';
import {
  changeEmail,
  deleteAccount,
  requestPasswordReset,
  resendVerification,
  updateProfileName,
} from '../api/auth';
import { eraseAllEntries } from '../api/entries';
import { fetchEntitlement, openBillingPortal, type Entitlement } from '../api/billing';
import { fetchSettings, updateSettings, SETTINGS_DEFAULTS, type UserSettings } from '../api/settings';
import { Notice, Spinner } from '../components/ui';
import {
  ConfirmDialog,
  SettingsAction,
  SettingsLink,
  SettingsSection,
  SettingsStatic,
  SettingsSwitch,
  type ConfirmSpec,
} from '../components/SettingsKit';
import {
  IconBell,
  IconBook,
  IconBrain,
  IconCheck,
  IconGear,
  IconImage,
  IconLink,
  IconLock,
  IconLogout,
  IconMail,
  IconPalette,
  IconShield,
  IconSliders,
  IconSpark,
  IconTrash,
  IconUser,
} from '../components/Icons';
import { ReaderSettingsSheet, useReaderSettings, type ReaderFont } from './ReaderSettings';

type Theme = 'dusk' | 'dawn';
type Accent = 'cool' | 'warm' | 'teal' | 'eclipse';
type Glass = 'frost' | 'clear';

const APPEARANCE_KEY = 'oreia.appearance';

interface Appearance {
  theme: Theme;
  accent: Accent;
  glass: Glass;
}

const APPEARANCE_DEFAULTS: Appearance = { theme: 'dusk', accent: 'cool', glass: 'frost' };

function loadAppearance(): Appearance {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    if (raw) return { ...APPEARANCE_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    // Fall through to the defaults.
  }
  return { ...APPEARANCE_DEFAULTS };
}

/** The sky currently in force, or null if this browser has never chosen one. */
export function storedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Appearance>;
    return parsed.theme === 'dawn' || parsed.theme === 'dusk' ? parsed.theme : null;
  } catch {
    return null;
  }
}

/**
 * Change the sky and remember it, leaving accent and glass alone.
 *
 * Onboarding's atmosphere screen needs both halves: it repaints live so the choice is visible, and
 * it has to persist, or the sky someone picked would quietly revert on their next visit.
 */
export function persistTheme(theme: Theme): void {
  const next = { ...loadAppearance(), theme };
  applyAppearance(next);
  try {
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(next));
  } catch {
    // Private browsing: the theme still holds for this session.
  }
}

/** Apply the appearance to <html>, which is where tokens.css reads its selectors from. */
export function applyAppearance(appearance: Appearance): void {
  const html = document.documentElement;
  html.dataset.theme = appearance.theme;
  html.dataset.accent = appearance.accent;
  html.dataset.glass = appearance.glass;
}

const ACCENT_LABELS: Record<Accent, string> = {
  cool: 'Violet',
  warm: 'Peach',
  teal: 'Turquoise',
  eclipse: 'Eclipse',
};

const SUPPORT_EMAIL = 'support@oraclethejournal.com';

/** Named so the Appearance row can show what's set without opening the sheet. */
const READER_FONT_LABELS: Record<ReaderFont, string> = {
  instrument: 'Instrument',
  newsreader: 'Newsreader',
  inter: 'Inter',
  mono: 'Geist Mono',
};

export function SettingsScreen() {
  const { user, signOut, lockVault } = useSession();
  const { entries, refresh } = useEntries();
  const navigate = useNavigate();

  const [appearance, setAppearance] = useState<Appearance>(loadAppearance);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [settings, setSettings] = useState<UserSettings>(SETTINGS_DEFAULTS);
  const [settingsReady, setSettingsReady] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const [reader, setReader] = useReaderSettings();
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [name, setName] = useState(user?.name ?? '');

  useEffect(() => {
    applyAppearance(appearance);
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(appearance));
  }, [appearance]);

  useEffect(() => {
    fetchEntitlement()
      .then(setEntitlement)
      .catch(() => setEntitlement(null));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchSettings(ac.signal)
      .then((value) => {
        setSettings(value);
        setSettingsReady(true);
      })
      .catch(() => {
        // The switches stay disabled rather than showing defaults as though they were read: a
        // "Pause memory-building" that reads Off when the truth is unknown is worse than one that
        // admits it could not load.
      });
    return () => ac.abort();
  }, []);

  /** Say something small and let it fade — a settings change rarely deserves a banner. */
  const flash = useCallback((message: string) => {
    setNote(message);
    setError(null);
    setTimeout(() => setNote((current) => (current === message ? null : current)), 3200);
  }, []);

  /**
   * Move the switch immediately, then put it back if the server disagrees.
   *
   * These two flags change what the nightly pass does, so a switch that shows the new position while
   * the server kept the old one is a lie about what Oreia is doing with the next thing you write.
   */
  const toggle = useCallback(
    async (patch: Partial<UserSettings>) => {
      const previous = settings;
      setSettings({ ...settings, ...patch });
      setError(null);
      try {
        setSettings(await updateSettings(patch));
      } catch {
        setSettings(previous);
        setError('That setting did not save. Check your connection and try again.');
      }
    },
    [settings],
  );

  async function manageBilling() {
    setBusy('portal');
    try {
      window.location.href = await openBillingPortal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open your billing portal.');
      setBusy(null);
    }
  }

  /* The saved name, or the one just saved. `user` is not refetched after a rename, so reading
     `user.name` alone would leave the row and the profile card showing the old name until reload. */
  const shownName = name.trim() || user?.name?.trim() || '';
  const initial = (shownName || user?.email || '?').trim().charAt(0).toUpperCase();
  const isPremium = entitlement?.isPremium ?? false;

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Settings</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      {/* Screen-level feedback sits above every section: a toggle near the bottom that failed still
          needs to be able to say so somewhere the user is looking. */}
      {error ? (
        <div style={{ marginTop: 16 }} role="alert">
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}
      {note ? (
        <div style={{ marginTop: 16 }} role="status">
          <Notice>{note}</Notice>
        </div>
      ) : null}

      {/* ── Profile ─────────────────────────────────────────────────────────────────────────────── */}
      <section className="oset-section">
        <div className="ocard ocard-light oset-profile">
          <span className="oset-avatar" aria-hidden>{initial}</span>
          <div className="oset-profile-text">
            <span className="oset-profile-name">{shownName || 'Add your name'}</span>
            <span className="oset-profile-mail">{user?.email}</span>
          </div>
          <span className="oset-profile-count">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        {/* A tappable row like Email rather than an inline field with its own save button: the name
            under the label is the current value, and changing it happens in the same dialog every
            other single-value change on this screen uses. */}
        <div className="oset-group" style={{ marginTop: 10 }}>
          <SettingsAction
            icon={<IconUser />}
            label="Name"
            hint={shownName || 'What should Oreia call you?'}
            onClick={() =>
              setConfirm({
                title: 'Your name',
                body: 'Never shown to anyone — Oreia is only ever for you.',
                confirmLabel: 'Save name',
                input: {
                  label: 'Name',
                  placeholder: 'What should Oreia call you?',
                  initial: shownName,
                },
                onConfirm: async (value) => {
                  await updateProfileName(value);
                  setName(value);
                  flash('Saved.');
                },
              })
            }
          />
        </div>
      </section>

      {/* ── Premium ─────────────────────────────────────────────────────────────────────────────── */}
      <section className="oset-section">
        {entitlement === null ? (
          <div className="ocard ocard-light">
            <Spinner label="Checking your plan…" />
          </div>
        ) : isPremium ? (
          <>
            <div className="ocard ocard-light oset-premium is-active">
              <span className="oset-premium-icon"><IconSpark /></span>
              <div className="oset-profile-text">
                <span className="oset-label">Oreia Premium</span>
                <span className="oset-hint">
                  Active — thank you for going deeper.
                  {entitlement.expiresAt
                    ? ` Renews ${new Date(entitlement.expiresAt).toLocaleDateString()}.`
                    : ''}
                </span>
              </div>
            </div>

            {/* Where to cancel has to be findable, and it is not the same place for both stores. */}
            <div className="oset-group" style={{ marginTop: 10 }}>
              {/* Premium accounts had no route to the comparison at all: the only link to it was
                  the upgrade card, which is exactly the thing that stops rendering once you pay. */}
              <SettingsAction
                icon={<IconSpark />}
                label="Plans and pricing"
                hint="What Free and Premium each include"
                onClick={() => navigate('/pricing')}
              />
              {entitlement.source === 'apple' ? (
                <SettingsStatic
                  icon={<IconSpark />}
                  label="Manage subscription"
                  hint="Bought through the App Store — change or cancel it on your iPhone under Settings → Apple Account → Subscriptions."
                />
              ) : (
                <SettingsAction
                  icon={<IconSpark />}
                  label="Manage billing"
                  hint="Payment method, invoices, or cancel"
                  disabled={busy === 'portal'}
                  onClick={() => void manageBilling()}
                />
              )}
            </div>
          </>
        ) : (
          // Sends people to the comparison rather than straight into Stripe: the previous card
          // jumped to checkout, so the first place the plan was described in full was the payment
          // page. The old hint also promised a deeper portrait and a monthly letter, neither of
          // which the Worker gates on payment — see pricing-tiers.ts.
          <Link to="/pricing" className="ocard ocard-light oset-premium oset-premium-link">
            <span className="oset-premium-icon"><IconSpark /></span>
            <div className="oset-profile-text">
              <span className="oset-label">Go deeper with Premium</span>
              <span className="oset-hint">
                Dream readings, recurring patterns, up to 10 DreamVisions a month, and the Weekly Mirror. Monthly fair use applies.
              </span>
            </div>
            <span className="octa oset-premium-cta">See plans</span>
          </Link>
        )}
      </section>

      {/* ── Account ─────────────────────────────────────────────────────────────────────────────── */}
      <SettingsSection icon={<IconUser />} title="Account">
        <SettingsAction
          icon={<IconMail />}
          label="Email"
          hint={user?.email}
          onClick={() =>
            setConfirm({
              title: 'Change email',
              body: "We'll send a confirmation link to your new address before it takes effect.",
              confirmLabel: 'Send confirmation',
              input: { label: 'New email', placeholder: 'new@email.com', type: 'email' },
              onConfirm: async (value) => {
                await changeEmail(value);
                flash(`Confirmation sent to ${value}. Your email changes once you follow that link.`);
              },
            })
          }
        />
        {user?.emailVerified ? (
          <SettingsStatic icon={<IconCheck />} label="Email verified" value="Verified" />
        ) : (
          <SettingsAction
            icon={<IconMail />}
            label="Verify email"
            hint="Resend the verification link"
            chevron={false}
            onClick={() => {
              if (!user?.email) return;
              resendVerification(user.email)
                .then(() => flash('Verification email sent.'))
                .catch(() => setError("Couldn't send that — try again in a minute."));
            }}
          />
        )}
        <SettingsAction
          icon={<IconLock />}
          label="Password"
          hint="Encrypts everything — we can never see it"
          onClick={() =>
            setConfirm({
              title: 'Change your password',
              body: (
                <>
                  Your password encrypts your journal, so changing it also re-wraps your key. We do
                  that through an emailed link, which keeps both in step — a password that no longer
                  matched your key would leave your entries unreadable.
                  <br />
                  <br />
                  We'll send the link to <strong>{user?.email}</strong>.
                </>
              ),
              confirmLabel: 'Email me a link',
              onConfirm: async () => {
                if (!user?.email) throw new Error('No email on this account.');
                await requestPasswordReset(user.email);
                flash('Check your email for the link to set a new password.');
              },
            })
          }
        />
      </SettingsSection>

      {/* ── Privacy & Security ──────────────────────────────────────────────────────────────────── */}
      <SettingsSection
        icon={<IconShield />}
        title="Privacy & Security"
        footnote={
          <>
            Read our <a href="https://oreiathejournal.com/privacy">Privacy Policy</a> for exactly
            what we can and cannot see.
          </>
        }
      >
        {/* Not "end-to-end encryption", which this is not, and not on a screen whose own footnote
            links to the policy saying so. The server holds server_wrapped_dek under VAULT_KEK and
            opens it on two automated paths — writing reflections, and re-sealing the key when someone
            resets their password. What is true, and still the strong part, is where the key is made. */}
        <SettingsStatic icon={<IconShield />} label="Encrypted on your device" value="On" />
        {/* The hint stopped being true the moment password reset could recover a vault. What is still
            singular about the phrase is that it is the way back in that needs nobody here. */}
        <SettingsAction
          icon={<IconLock />}
          label="Recovery phrase"
          hint="The way back in that runs through nobody else"
          onClick={() =>
            setConfirm({
              title: 'Recovery phrase',
              body: (
                <>
                  Your recovery phrase was shown once when you created your account. For your
                  security it can't be displayed again.
                  <br />
                  <br />
                  If you've lost it, sign out and choose <strong>Restore</strong> to enter it on a new
                  device — or set a new password by email while you're still signed in here.
                </>
              ),
              confirmLabel: 'Understood',
              onConfirm: () => undefined,
            })
          }
        />
        <SettingsAction
          icon={<IconLock />}
          label="Lock this browser"
          hint="Forget the key here — you'll unlock again with your password or phrase"
          chevron={false}
          onClick={() => void lockVault()}
        />
        <SettingsAction
          icon={<IconLogout />}
          label="Sign out"
          chevron={false}
          onClick={() =>
            setConfirm({
              title: 'Sign out?',
              body: "You'll need your password to unlock Oreia again.",
              confirmLabel: 'Sign out',
              onConfirm: () => signOut(),
            })
          }
        />
      </SettingsSection>

      {/* ── Higher Self ─────────────────────────────────────────────────────────────────────────── */}
      <SettingsSection
        icon={<IconBrain />}
        title="Higher Self"
        footnote={
          settingsReady
            ? undefined
            : "These two couldn't be loaded, so they're read-only until the connection comes back."
        }
      >
        <SettingsSwitch
          label="Pause memory-building"
          hint="Keep journaling without new notes being made about you"
          checked={settings.memoryPaused}
          disabled={!settingsReady}
          onChange={(next) => void toggle({ memoryPaused: next })}
        />
        <SettingsSwitch
          label="Link dreams & waking life"
          hint="Let Oreia read one against the other"
          checked={settings.linkingEnabled}
          disabled={!settingsReady}
          onChange={(next) => void toggle({ linkingEnabled: next })}
        />
        <SettingsRowLink to="/self" icon={<IconBrain />} label="What Oreia holds for you" hint="Review, affirm or correct" />
        <SettingsRowLink to="/dreams" icon={<IconImage />} label="Dream Gallery" hint="Browse your saved Dream Visions" />
        <SettingsRowLink to="/symbols" icon={<IconLink />} label="Symbol Vault" hint="Every symbol and where it came from" />
      </SettingsSection>

      {/* ── Notifications ───────────────────────────────────────────────────────────────────────── */}
      <SettingsSection icon={<IconBell />} title="Reminders">
        <SettingsStatic
          icon={<IconBell />}
          label="Evening reflection · Morning dream capture"
          hint="Set these on your iPhone for now. A browser can only send reminders while it's running, so nudges belong on the phone until Oreia has real web push."
        />
      </SettingsSection>

      {/* ── Appearance ──────────────────────────────────────────────────────────────────────────── */}
      <SettingsSection icon={<IconPalette />} title="Appearance">
        <SettingsChoice
          label="Background"
          hint="Dawn is light, Dusk is dark"
          value={appearance.theme}
          options={[
            { value: 'dusk', label: 'Dusk' },
            { value: 'dawn', label: 'Dawn' },
          ]}
          onChange={(theme) => setAppearance({ ...appearance, theme: theme as Theme })}
        />
        <SettingsChoice
          label="Accent"
          value={appearance.accent}
          options={(Object.keys(ACCENT_LABELS) as Accent[]).map((value) => ({
            value,
            label: ACCENT_LABELS[value],
          }))}
          onChange={(accent) => setAppearance({ ...appearance, accent: accent as Accent })}
        />
        <SettingsChoice
          label="Glass"
          hint="How much the panels blur what's behind them"
          value={appearance.glass}
          options={[
            { value: 'frost', label: 'Frost' },
            { value: 'clear', label: 'Clear' },
          ]}
          onChange={(glass) => setAppearance({ ...appearance, glass: glass as Glass })}
        />
        <SettingsAction
          icon={<IconSliders />}
          label="Reading & writing"
          hint={`${READER_FONT_LABELS[reader.font]} · ${reader.textSize}px`}
          onClick={() => setReaderOpen(true)}
        />
      </SettingsSection>

      {/* ── Data ────────────────────────────────────────────────────────────────────────────────── */}
      <SettingsSection icon={<IconGear />} title="Data & Storage">
        <SettingsAction
          icon={<IconImage />}
          label="Clear cached images"
          hint="Frees space here. Your entries and photos are untouched."
          chevron={false}
          onClick={async () => {
            // Only the HTTP caches. IndexedDB deliberately survives: the wrapped key lives there, and
            // clearing it would present as being mysteriously logged out of your own journal.
            try {
              const names = await caches.keys();
              await Promise.all(names.map((n) => caches.delete(n)));
              flash('Cached images cleared.');
            } catch {
              setError('Could not clear the cache in this browser.');
            }
          }}
        />
        <SettingsAction
          icon={<IconTrash />}
          label="Erase all entries"
          hint="Keeps your account — removes everything you've written"
          danger
          onClick={() =>
            setConfirm({
              title: 'Erase all entries?',
              body: 'This permanently deletes every entry and dream, and resets your portrait — the observations, symbols and links built from them go too. This cannot be undone.',
              confirmLabel: 'Erase everything',
              typed: 'ERASE',
              danger: true,
              onConfirm: async () => {
                const count = await eraseAllEntries();
                await refresh();
                flash(`${count} ${count === 1 ? 'entry' : 'entries'} erased.`);
              },
            })
          }
        />
      </SettingsSection>

      {/* ── Destructive, unlabelled — as on iOS ─────────────────────────────────────────────────── */}
      <SettingsSection>
        <SettingsAction
          icon={<IconTrash />}
          label="Delete account"
          hint="Everything, permanently — there is no recovery"
          danger
          onClick={() =>
            setConfirm({
              title: 'Delete account?',
              body: 'Your account, every entry, your portrait, and all encrypted data are permanently destroyed. There is no recovery.',
              confirmLabel: 'Delete my account',
              typed: 'DELETE',
              danger: true,
              onConfirm: async () => {
                await deleteAccount();
                await signOut();
                window.location.href = '/login';
              },
            })
          }
        />
      </SettingsSection>

      {/* ── About ───────────────────────────────────────────────────────────────────────────────── */}
      <SettingsSection icon={<IconBook />} title="About">
        <SettingsLink href="https://oreiathejournal.com/privacy" label="Privacy Policy" />
        <SettingsLink href="/terms" label="Terms of Use" />
        <SettingsLink href={`mailto:${SUPPORT_EMAIL}`} label="Help & Support" hint={SUPPORT_EMAIL} />
        <SettingsStatic label="Version" value={__APP_VERSION__} />
      </SettingsSection>

      <p className="oset-signoff">
        Oreia is built to keep your inner life private.
        <br />
        <span className="otext-mute">made with care · 2026</span>
      </p>

      <div style={{ height: 60 }} />

      <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />
      <ReaderSettingsSheet
        open={readerOpen}
        settings={reader}
        onChange={setReader}
        onClose={() => setReaderOpen(false)}
      />
    </>
  );
}

/** An in-app navigation row. Separate from SettingsLink so it routes instead of reloading the app. */
function SettingsRowLink({
  to,
  icon,
  label,
  hint,
}: {
  to: string;
  icon?: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <Link className="oset-row is-tappable" to={to}>
      {icon ? <span className="oset-icon">{icon}</span> : null}
      <span className="oset-body">
        <span className="oset-label">{label}</span>
        {hint ? <span className="oset-hint">{hint}</span> : null}
      </span>
      <span className="oset-chev" aria-hidden>
        ›
      </span>
    </Link>
  );
}

/**
 * A row whose control is a small set of choices.
 *
 * A radiogroup rather than a row of buttons: arrow keys then move between the options and the group
 * announces as one control with one selected value, which is what it is.
 */
function SettingsChoice({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange(value: string): void;
}) {
  return (
    <div className="oset-row oset-row-choice">
      <span className="oset-body">
        <span className="oset-label" id={`choice-${label}`}>{label}</span>
        {hint ? <span className="oset-hint">{hint}</span> : null}
      </span>
      <div className="oset-chips" role="radiogroup" aria-labelledby={`choice-${label}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            className={`ochip${value === option.value ? ' active' : ''}`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
